import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, MessageCircleMore, Sparkles } from "lucide-react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";
import { cls } from "../utils/helpers";
import { WHATSAPP_NUM, REGISTER_URL } from "../utils/constants";
import { apiClient } from "../utils/apiClient";

function openWhatsApp(preset) {
  const num = WHATSAPP_NUM.replace(/\D/g, "");
  const text = encodeURIComponent(
    preset || "Bonjour, je souhaite en savoir plus sur vos tarifs."
  );
  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
}

export default function PricingPage({ p, t }) {
  const [loading, setLoading] = useState(true);
  const [pricingPlans, setPricingPlans] = useState([]);

  useEffect(() => {
    async function fetchPricing() {
      try {
        const response = await apiClient.fetchPlans();

        if (response.success && response.plans) {
          const formattedPlans = response.plans.map((plan, index) => {
            const currencySymbol = plan.currency === 'EUR' ? '€' : plan.currency;
            const priceFormatted = parseFloat(plan.price).toFixed(0);
            const overagePriceFormatted = parseFloat(plan.overage_price).toFixed(0);

            const features = [
              plan.description,
              `Up to ${plan.max_users} users`,
              `${currencySymbol}${overagePriceFormatted} ${t('pricing.taxes')}${t('pricing.per_user_month')} for additional users`,
              "Priority support",
              "Regular updates"
            ];

            return {
              id: plan.name,
              name: plan.description,
              price: priceFormatted,
              currency: currencySymbol,
              description: `1 admin + ${plan.max_users} users`,
              features: features,
              highlighted: index === 1,
              maxUsers: plan.max_users,
              overagePrice: plan.overage_price
            };
          });

          setPricingPlans(formattedPlans);
        }
      } catch (error) {
        console.error('Failed to fetch pricing plans:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchPricing();
  }, [t]);

  const contactPlan = {
    id: "contact",
    name: t("pricing.contact.name"),
    description: t("pricing.contact.desc"),
    features: [
      t("pricing.contact.feat1"),
      t("pricing.contact.feat2"),
      t("pricing.contact.feat3"),
      t("pricing.contact.feat4")
    ],
    isContact: true,
  };

  return (
    <div className={p.page}>
      <Breadcrumb
        items={[
          { label: t("nav.home"), href: "/" },
          { label: t("pricing.title") },
        ]}
        t={t}
        p={p}
      />

      <section className="py-12 md:py-16">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className={cls("text-3xl md:text-4xl font-bold mb-4", p.h1)}>
              {t("pricing.title")}
            </h1>
            <p className={cls("text-lg", p.subtitle)}>
              {t("pricing.subtitle")}
            </p>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={cls(
                    "rounded-2xl p-8 shadow-sm animate-pulse",
                    p.card
                  )}
                >
                  <div className="text-center mb-6">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto mb-4"></div>
                    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto mb-3"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
              {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={cls(
                  "rounded-2xl p-6 shadow-sm relative",
                  plan.highlighted
                    ? "ring-2 ring-indigo-600"
                    : "ring-1 ring-black/5 dark:ring-white/10",
                  p.card
                )}
              >
                {plan.highlighted && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                    {t("pricing.popular")}
                  </span>
                )}

                <div className="text-center mb-6">
                  <h2
                    className={cls(
                      "text-xl font-bold mb-2",
                      p.cardTitle
                    )}
                  >
                    {plan.name}
                  </h2>
                  <div className="flex items-baseline justify-center gap-1">
                    {plan.currency && (
                      <span className="text-xl font-bold">{plan.currency}</span>
                    )}
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className={cls("text-sm", p.cardP)}>{t('pricing.taxes')}</span>
                  </div>
                  <p className={cls("text-xs mt-1", p.cardP)}>{t('pricing.per_project_month')}</p>
                  <p className={cls("mt-2 text-xs", p.cardP)}>
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-2 mb-6">
                  {Array.isArray(plan.features) && plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check
                        size={16}
                        className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5"
                      />
                      <span className="text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cls(
                    "block text-center rounded-lg px-4 py-2 text-xs font-semibold transition-colors",
                    plan.highlighted
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : p.ctaSecondary
                  )}
                >
                  {t("pricing.cta")}
                </a>
              </div>
              ))}
              <div
                key={contactPlan.id}
                className={cls(
                  "rounded-2xl p-6 shadow-sm relative ring-2 ring-indigo-500",
                  p.card
                )}
              >
                <div className="text-center mb-6">
                  <h2
                    className={cls(
                      "text-xl font-bold mb-2",
                      p.cardTitle
                    )}
                  >
                    {contactPlan.name}
                  </h2>
                  <div className="flex items-center justify-center my-4">
                    <div className="relative">
                      <Sparkles size={48} className="text-indigo-600 dark:text-indigo-400" strokeWidth={2} />
                    </div>
                  </div>
                  <p className={cls("mt-2 text-xs", p.cardP)}>
                    {contactPlan.description}
                  </p>
                </div>

                <ul className="space-y-2 mb-6">
                  {contactPlan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check
                        size={16}
                        className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5"
                      />
                      <span className="text-xs">{typeof feature === 'object' ? feature.label : feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className="block text-center rounded-lg px-4 py-2 text-xs font-semibold transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  {t("pricing.contact.cta")}
                </Link>
              </div>
            </div>
          )}

          <div className="mt-16 max-w-3xl mx-auto">
            <div className={cls("rounded-2xl p-8 text-center", p.card)}>
              <h3 className={cls("text-xl font-bold mb-4", p.cardTitle)}>
                {t("pricing.help.title")}
              </h3>
              <p className={cls("mb-6", p.cardP)}>
                {t("pricing.help.desc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => openWhatsApp()}
                  className={cls(
                    "inline-flex items-center gap-2 justify-center",
                    p.ctaPrimary
                  )}
                >
                  <MessageCircleMore size={18} />
                  {t("btn.whatsapp")}
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
